export type RgbTuple = [number, number, number];

export const STORE_CHECKOUT_LIGHT_COLOR_ERROR = 'Try to add some darker shade for your store';

export const STORE_CHECKOUT_COLOR_PICKER_DEFAULT_HEX = '#4698EA';

/** User may add at most one custom swatch via the + color picker (re-open updates it). */
export const STORE_CHECKOUT_MAX_CUSTOM_SWATCH_COLORS = 1;

export function parseHexToRgb(hex: string | null | undefined): RgbTuple | null {
  if (hex == null || hex === '') {
    return null;
  }
  let h = hex.toString().trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    return null;
  }
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function normalizeHexColor(raw: string | null | undefined): string | null {
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  const trimmed = String(raw).trim();
  const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (!hexPattern.test(trimmed)) {
    return null;
  }
  let h = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (h.length === 3) {
    h = h
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const normalized = `#${h}`.toUpperCase();
  return parseHexToRgb(normalized) ? normalized : null;
}

export function normalizeCustomSwatchColors(colors: readonly string[]): string[] {
  if (!colors?.length) {
    return [];
  }
  const first = normalizeHexColor(colors[0]);
  return first ? [first] : [];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = 60 * (((gn - bn) / delta) % 6);
    } else if (max === gn) {
      h = 60 * ((bn - rn) / delta + 2);
    } else {
      h = 60 * ((rn - gn) / delta + 4);
    }
  }
  if (h < 0) {
    h += 360;
  }
  const s = max === 0 ? 0 : delta / max;
  return { h, s, v: max };
}

export function hsvToRgb(h: number, s: number, v: number): RgbTuple {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rn = 0;
  let gn = 0;
  let bn = 0;
  if (h >= 0 && h < 60) {
    rn = c;
    gn = x;
  } else if (h < 120) {
    rn = x;
    gn = c;
  } else if (h < 180) {
    gn = c;
    bn = x;
  } else if (h < 240) {
    gn = x;
    bn = c;
  } else if (h < 300) {
    rn = x;
    bn = c;
  } else {
    rn = c;
    bn = x;
  }
  return [Math.round((rn + m) * 255), Math.round((gn + m) * 255), Math.round((bn + m) * 255)];
}

export function hsvToHex(h: number, s: number, v: number): string {
  const [r, g, b] = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
}

export function isLightStoreCheckoutColor(hex: string | null | undefined): boolean {
  const rgb = parseHexToRgb(hex);
  if (!rgb) {
    return false;
  }
  const [r, g, b] = rgb;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 225;
}
